// application/useVisitaDetalle.js  (detalle enriquecido; imágenes vía useImagenesDetalle). Sin JSX, sin Swal.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { visitasRepository } from '../infrastructure/visitas.repository';
import { catalogosRepository } from '../../shared/infrastructure/catalogos.repository';
import { mediaRepository } from '../../shared/infrastructure/media.repository';
import { archivoRepository } from '../../shared/infrastructure/archivo';
import { useDescargarPdf } from '../../shared/application/useDescargarPdf';
import { useFotosProductores } from '../../shared/application/useFotosProductores';
import { conDetalle } from '../domain/visita.detalle';
import { useImagenesDetalle } from '../../shared/application/useImagenesDetalle';
import { idProductor } from '../domain/visita';

const EMPTY = { entity: null, catalogs: {}, status: 'idle', error: null };

export const useVisitaDetalle = ({
  uuid,
  repository = visitasRepository,
  catalogos = catalogosRepository,
  media = mediaRepository,
  archivo = archivoRepository,
} = {}) => {
  const [state, setState] = useState(EMPTY);
  const mounted = useRef(true);
  const requestId = useRef(0);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const reload = useCallback(async () => {
    if (!uuid) return;
    const id = ++requestId.current;
    setState(prev => ({ ...prev, status: 'loading', error: null }));
    try {
      const entity = await repository.get(uuid);
      // Parcelas solo del productor de la visita (nunca el catálogo completo).
      const [productores, parcelas, usuarios] = await Promise.all([
        catalogos.productores(entity.id_ciclo),
        catalogos.parcelasDeProductor(entity.id_parcela !== null ? idProductor(entity) : null),
        catalogos.usuarios(),
      ]);
      if (mounted.current && id === requestId.current) {
        setState({
          entity,
          catalogs: { productores, parcelas, usuarios },
          status: 'success',
          error: null,
        });
      }
    } catch (error) {
      if (mounted.current && id === requestId.current) setState({ ...EMPTY, status: 'error', error });
    }
  }, [uuid, repository, catalogos]);

  useEffect(() => {
    reload();
  }, [reload]);

  const data = useMemo(() => (state.entity ? conDetalle(state.entity, state.catalogs) : null), [state.entity, state.catalogs]);

  const uuidsProductor = useMemo(() => (data && data.productor ? [data.productor.uuid] : []), [data]);
  const { fotos } = useFotosProductores(uuidsProductor);
  const { descargar, descargando } = useDescargarPdf(repository.descargarPdf);
  /** PDF oficial de esta visita -> { blob, nombre } (null si ya hay una descarga en curso). Lanza DomainError. */
  const descargarPdf = useCallback(() => descargar(uuid), [descargar, uuid]);

  const setEntity = useCallback(transformar => {
    setState(prev => (prev.entity ? { ...prev, entity: transformar(prev.entity) } : prev));
  }, []);
  const imagenes = useImagenesDetalle({ tipo: 'visita', entity: state.entity, setEntity, media, archivo });

  return { data, fotos, status: state.status, error: state.error, reload, descargarPdf, descargandoPdf: descargando, ...imagenes };
};

export default useVisitaDetalle;
